import { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useNavigationBlocker } from '../../hooks/useNavigationBlocker';
import { useProjectScope } from '../../hooks/useProjectScope';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { z } from 'zod';

import { Toast } from 'sharedUi/Toast';
import { InputSwitch } from 'sharedUi/InputSwitch';
import { Button } from 'sharedUi/Button';
import { ProgressBar } from 'sharedUi/ProgressBar';
import { uploadFile, getDownloadUrl } from 'sharedUi/fileUploadService';
import ImagePreviewOverlay from 'sharedUi/ImagePreviewOverlay';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';

import FormInput from '../../components/forms/form-input';
import FormSelect from '../../components/forms/form-select';
import FormSearchableSelect from '../../components/forms/form-searchable-select';
import FormFileUpload from '../../components/forms/form-file-upload';
// Commented out - unitPersonnelList form field removed
// import FormSearchableMultiSelect from '../../components/forms/form-searchable-multi-select';
import DiscardChangesDialog from '../../components/dialogs/discard-changes-dialog';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { JOB_NAMES } from '../../constants/jobNames';
import { unitsService } from '../../services/units.service';
import { masterService } from '../../services/master.service';
import { ministriesService } from '../../services/ministries.service';
import { projectsService } from '../../services/projects.service';
import { usersService } from '../../services/users.service';
import type { RootState } from '../../utils/main-fe-auth';
import { extractErrorMessage } from '../../utils/error-handler';
import { PageSkeleton } from '../../components/common/panel';
import ScreenHeader from '../../components/common/screen-header';

/**
 * Validation Schema - Zod
 */
const unitSchema = z.object({
  name: z
    .string()
    .min(2, 'Unit name must be at least 2 characters')
    .max(200, 'Unit name must not exceed 200 characters')
    .trim(),

  unitCode: z
    .string()
    .min(1, 'Unit code is required')
    .max(100, 'Unit code must not exceed 100 characters')
    .regex(/^[a-zA-Z0-9\s&-]*$/, 'Unit code must contain only letters, numbers, spaces, & and -'),

  appId: z
    .string()
    .optional()
    .or(z.literal('')),

  ministryId: z
    .string()
    .optional()
    .or(z.literal('')),

  departmentId: z
    .string()
    .optional()
    .or(z.literal('')),

  unitTypeId: z
    .string()
    .optional()
    .or(z.literal('')),

  parentUnitId: z
    .string()
    .optional()
    .nullable()
    .or(z.literal('')),

  logo: z
    .any()
    .optional()
    .nullable(),

  address1: z
    .string()
    .max(500, 'Address line 1 must not exceed 500 characters')
    .trim()
    .optional()
    .nullable(),

  address2: z
    .string()
    .max(500, 'Address line 2 must not exceed 500 characters')
    .trim()
    .optional()
    .nullable(),

  city: z
    .string()
    .max(100, 'City must not exceed 100 characters')
    .trim()
    .optional()
    .nullable(),

  districtId: z
    .string()
    .min(1, 'District is required'),

  zip: z
    .string()
    .optional()
    .refine(
      (val) => !val || val === '' || /^[0-9]{6}$/.test(val),
      { message: 'ZIP code must be 6 digits' }
    )
    .or(z.literal('')),

  email: z
    .string()
    .min(1, 'Email is required')
    .email('Invalid email format')
    .max(255, 'Email must not exceed 255 characters')
    .trim(),

  phone: z
    .string()
    .trim()
    .optional()
    .refine(
      (val) => {
        if (!val || val === '') return true;
        // Split by comma and validate each phone entry
        const phones = val.split(',').map(p => p.trim()).filter(Boolean);
        // Each entry may contain numbers, spaces and hyphens only
        return phones.every(phone => /^[0-9\s-]+$/.test(phone));
      },
      { message: 'Phone numbers may contain only numbers, spaces and hyphens (comma-separated for multiple)' }
    )
    .or(z.literal('')),

  // Responsible user (unit head) account - optional FK to user_master
  responsibleUserId: z
    .string()
    .optional()
    .or(z.literal('')),

  // Plain title string (NOT a designation id) - optional
  responsiblePersonTitle: z
    .string()
    .max(255, 'Title must not exceed 255 characters')
    .optional()
    .or(z.literal('')),

  proxyUserId: z
    .string()
    .optional()
    .nullable(),

  unitPersonnelList: z
    .preprocess(
      (val) => {
        if (Array.isArray(val)) {
          return val.filter(v => v !== null && v !== undefined && v !== '');
        }
        return [];
      },
      z.array(z.string()).optional().default([])
    ),

  isVirtual: z
    .boolean()
    .default(false),
});

type UnitFormData = z.infer<typeof unitSchema>;

/**
 * UnitForm Component - Production Grade
 */
const UnitForm = () => {
  const { id, isReady, navigateToList } = useSecureNavigation({
    entity: 'units',
    basePath: '/units',
  });
  const isEditMode = Boolean(id);
  const queryClient = useQueryClient();
  const toast = useRef<Toast>(null);
  // Stops autosave rewriting the draft after a submit clears it (the reason the
  // "restore?" banner reappeared right after a create/update).
  const submittedRef = useRef(false);

  // Get pre-selected values from URL query params (when coming from Units list "Add Child Unit")
  const [searchParams] = useSearchParams();
  const preSelectedParentUnitId = searchParams.get('parentUnitId') || '';
  const preSelectedParentUnitName = searchParams.get('parentUnitName') || '';
  const preSelectedDepartmentId = searchParams.get('departmentId') || '';
  const preSelectedDistrictId = searchParams.get('districtId') || '';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const imageOverlayRef = useRef<any>(null); // Ref for ImagePreviewOverlay
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null); // State for preview URL

  // Logo metadata state - stores file ID, filename and direct URL
  const [logoMeta, setLogoMeta] = useState<{ id: string; filename: string; url: string } | null>(null);

  // Cleanup preview URL on unmount or change (only for blob URLs)
  useEffect(() => {
    return () => {
      if (previewImageUrl && previewImageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewImageUrl);
      }
    };
  }, [previewImageUrl]);


  // Get current user ID from Redux store
  const currentUser = useSelector((state: RootState) => state.auth.user);
  /* Who is acting, by ACCOUNT id. `/users/me` returns a PERSONNEL profile, and a
     standalone account (no personnel record) legitimately comes back with
     `_id: null` — checking that alone made it impossible for such a user to
     create a unit at all: the form refused before it ever sent the request. */
  const actorId = currentUser?.account?._id || currentUser?._id;

  const DRAFT_KEY = `unit-form-draft-${id || 'create'}`;

  // State
  const [showDraftBanner, setShowDraftBanner] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [draftData, setDraftData] = useState<any>(null);

  // File Upload State
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);

  // Form setup
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<UnitFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(unitSchema) as any,
    defaultValues: {
      name: '',
      unitCode: '',
      appId: '',
      ministryId: '',
      departmentId: preSelectedDepartmentId,
      unitTypeId: '',
      parentUnitId: preSelectedParentUnitId,
      logo: null,
      address1: '',
      address2: '',
      city: '',
      districtId: preSelectedDistrictId,
      zip: '',
      email: '',
      phone: '',
      responsibleUserId: '',
      responsiblePersonTitle: '',
      proxyUserId: '',
      unitPersonnelList: [],
      isVirtual: false,
    },
    mode: 'onChange',
  });

  const { showLeaveDialog, confirmLeave, cancelLeave, handleNavigate } = useNavigationBlocker({ when: isDirty });

  const formValues = watch();

  // Watch the hierarchy fields to drive the cascade (ministry → department → unit type)
  const selectedUnitTypeId = watch('unitTypeId');
  const selectedMinistryId = watch('ministryId');
  const selectedDepartmentId = watch('departmentId');
  const selectedDistrictId = watch('districtId');
  const selectedParentUnitId = watch('parentUnitId');

  // Fetch dropdown data
  const { data: ministries = [], isLoading: ministriesLoading } = useQuery({
    queryKey: ['ministries'],
    queryFn: ministriesService.getAllForDropdown,
  });

  const { data: departments = [], isLoading: departmentsLoading } = useQuery({
    queryKey: ['departments'],
    queryFn: masterService.getDepartments,
  });

  // Fetch all unit types; filtered client-side by the selected department below.
  const { data: unitTypes = [], isLoading: unitTypesLoading } = useQuery({
    queryKey: ['unitTypes'],
    queryFn: masterService.getUnitTypes,
  });

  const { data: districts = [], isLoading: districtsLoading } = useQuery({
    queryKey: ['districts'],
    queryFn: masterService.getDistricts,
  });

  const { data: units = [], isLoading: unitsLoading } = useQuery({
    queryKey: ['units', 'with-unitType'],
    queryFn: masterService.getUnitsWithUnitType,
  });

  // Projects the caller may create a unit in (platform admin = all, else own).
  const { data: accessibleProjects = [] } = useQuery({
    queryKey: ['projects', 'accessible'],
    queryFn: projectsService.getAccessible,
    staleTime: 5 * 60 * 1000,
  });
  const projectOptions = useMemo(
    () => accessibleProjects.map((p) => ({
      value: p._id,
      label: p.appCode ? `${p.name} (${p.appCode})` : p.name,
    })),
    [accessibleProjects]
  );
  const projectLocked = accessibleProjects.length <= 1;
  const selectedAppId = watch('appId');
  // Default to the only accessible project (or the first) when creating.
  useEffect(() => {
    if (!isEditMode && !selectedAppId && accessibleProjects.length) {
      setValue('appId', accessibleProjects[0]._id);
    }
  }, [isEditMode, selectedAppId, accessibleProjects, setValue]);

  // Project-scoped dropdown helpers (filter to selected project + global, tag each
  // option with its owning project). Shared with the department / unit-type forms.
  const { belongsToProject, projectTag } = useProjectScope(selectedAppId, accessibleProjects);

  // Fetch user accounts for the Responsible User (Head) picker
  const { data: usersResponse } = useQuery({
    queryKey: ['users', 'all-for-unit-head'],
    queryFn: () => usersService.getAll({ page_size: 1000 }),
    staleTime: 5 * 60 * 1000,
  });

  // Options for the Responsible User (Head) searchable select
  const responsibleUserOptions = useMemo(() => {
    const list = usersResponse?.data ?? [];
    return list.map((u) => ({
      value: u._id,
      label: u.userId || u._id,
    }));
  }, [usersResponse]);

  // Fetch unit data for edit mode - wait for ID decryption
  const { data: unitData, isLoading: unitLoading } = useQuery({
    queryKey: ['unit', id],
    queryFn: () => unitsService.getById(id!),
    enabled: isEditMode && isReady && !!id,
  });

  // Set breadcrumb title to show unit name instead of UUID in edit mode
  useBreadcrumbTitle(isEditMode ? unitData?.name : null);

  // Populate form in edit mode
  useEffect(() => {
    if (unitData && isEditMode) {
      reset({
        name: unitData.name || '',
        unitCode: unitData.unitCode || '',
        appId: (unitData as any).appId || '',
        ministryId: unitData.ministryId || '',
        departmentId: unitData.departmentId || '',
        unitTypeId: unitData.unitTypeId || '',
        parentUnitId: unitData.parentUnitId || '',
        logo: unitData.logo || null,
        address1: unitData.address1 || '',
        address2: unitData.address2 || '',
        city: unitData.city || '',
        districtId: unitData.districtId || '',
        zip: unitData.zip || '',
        email: unitData.email || '',
        phone: Array.isArray(unitData.phone) ? unitData.phone.join(', ') : (unitData.phone || ''),
        responsibleUserId: unitData.responsibleUserId || '',
        responsiblePersonTitle: unitData.responsiblePersonTitle || '',
        proxyUserId: unitData.proxyUserId || '',
        unitPersonnelList: unitData.unitPersonnelList || [],
        isVirtual: unitData.isVirtual === true,
      });

      // Fetch logo metadata if logo exists
      if (unitData.logo) {
        const logoId = unitData.logo;
        const downloadApiUrl = getDownloadUrl(logoId);
        const token = localStorage.getItem('accessToken');

        // Call the download API to get the signed URL with filename
        fetch(downloadApiUrl, {
          method: 'GET',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
          .then((response) => response.json())
          .then((data) => {
            if (data?.success && data?.data?.url) {
              const signedUrl = data.data.url;
              // Extract filename from the signed URL
              // URL format: https://...blob.core.windows.net/...?...&rscd=attachment%3B%20filename%3D"actual-filename.png"
              let filename = logoId;
              try {
                // Try to extract filename from rscd parameter
                const urlObj = new URL(signedUrl);
                const rscd = urlObj.searchParams.get('rscd');
                if (rscd) {
                  // rscd format: attachment; filename="actual-filename.png" or attachment%3B%20filename%3D"actual-filename.png"
                  const decodedRscd = decodeURIComponent(rscd);
                  const filenameMatch = decodedRscd.match(/filename[=*]?"?([^";\s]+)"?/i);
                  if (filenameMatch && filenameMatch[1]) {
                    filename = decodeURIComponent(filenameMatch[1]);
                  }
                }
              } catch (e) {
                console.error('Failed to extract filename from URL:', e);
              }

              setLogoMeta({
                id: logoId,
                filename,
                url: signedUrl,
              });
            } else {
              // API call succeeded but no URL in response
              setLogoMeta({
                id: logoId,
                filename: logoId,
                url: downloadApiUrl,
              });
            }
          })
          .catch((err) => {
            console.error('Failed to fetch logo metadata:', err);
            // Fallback: use the ID as filename
            setLogoMeta({
              id: logoId,
              filename: logoId,
              url: downloadApiUrl,
            });
          });
      } else {
        setLogoMeta(null);
      }
    }
  }, [unitData, isEditMode, reset]);

  // Legacy units created before the cascade may have a department but no ministry.
  // Once departments load, back-fill the ministry from the unit's department so the
  // cascade shows a consistent chain (and the department select isn't left disabled).
  useEffect(() => {
    if (isEditMode && unitData && !unitData.ministryId && unitData.departmentId && departments.length) {
      const dept = departments.find(d => d._id === unitData.departmentId);
      if (dept?.ministryId) setValue('ministryId', dept.ministryId);
    }
  }, [isEditMode, unitData, departments, setValue]);

  // Transform options (memoized for performance). Each list is scoped to the
  // selected project (+ global) and tagged inline with its owning project.
  // Ministry is scoped to the selected project (+ global), like the other lists.
  // This relies on global departments being parented under global (or same-project)
  // ministries, so their parent stays visible; otherwise a global dept under a
  // different project's ministry would become unreachable.
  const ministryOptions = useMemo(
    () =>
      ministries
        .filter(m => belongsToProject(m.appId))
        .map(m => ({ value: m._id, label: m.name, ...projectTag(m.appId) })),
    [ministries, belongsToProject, projectTag]
  );

  // Department options cascade from the selected ministry.
  const departmentOptions = useMemo(
    () =>
      departments
        .filter(d => !selectedMinistryId || d.ministryId === selectedMinistryId)
        .filter(d => belongsToProject(d.appId))
        .map(d => ({ value: d._id, label: d.name, ...projectTag(d.appId) })),
    [departments, selectedMinistryId, belongsToProject, projectTag]
  );

  // Unit type options cascade from the selected department.
  const unitTypeOptions = useMemo(
    () =>
      unitTypes
        .filter(t => !selectedDepartmentId || t.departmentId === selectedDepartmentId)
        .filter(t => belongsToProject(t.appId))
        .map(t => ({ value: t._id, label: t.name, ...projectTag(t.appId) })),
    [unitTypes, selectedDepartmentId, belongsToProject, projectTag]
  );

  const districtOptions = useMemo(
    () =>
      districts
        .filter(d => belongsToProject(d.appId))
        .map(d => ({ value: d._id, label: d.name, ...projectTag(d.appId) })),
    [districts, belongsToProject, projectTag]
  );

  // Get selected unitType's level for filtering parent units
  const selectedUnitTypeLevel = useMemo(() => {
    if (!selectedUnitTypeId) return null;
    // Check both _id and id since API may return either format
    const unitType = unitTypes.find(t => t._id === selectedUnitTypeId || t.id === selectedUnitTypeId);
    return unitType?.level ?? null;
  }, [selectedUnitTypeId, unitTypes]);

  // When the project changes, clear any dependency selection that now belongs to
  // a different project (global/matching ones are kept). Skipped on the initial
  // load so a unit being edited keeps its saved, already-consistent values.
  const prevAppIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const prev = prevAppIdRef.current;
    prevAppIdRef.current = selectedAppId;
    // First run (undefined -> value) or no real change: nothing to reconcile.
    if (prev === undefined || prev === selectedAppId || !selectedAppId) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const findAppId = (list: any[], selectedId?: string | null) => {
      if (!selectedId) return undefined;
      const item = list.find((x) => x._id === selectedId || x.id === selectedId);
      return item?.appId as string | null | undefined;
    };
    const invalid = (itemAppId?: string | null) => itemAppId && itemAppId !== selectedAppId;

    if (invalid(findAppId(ministries, selectedMinistryId))) setValue('ministryId', '');
    if (invalid(findAppId(departments, selectedDepartmentId))) setValue('departmentId', '');
    if (invalid(findAppId(unitTypes, selectedUnitTypeId))) setValue('unitTypeId', '');
    if (invalid(findAppId(districts, selectedDistrictId))) setValue('districtId', '');
    if (invalid(findAppId(units, selectedParentUnitId))) setValue('parentUnitId', '');
  }, [
    selectedAppId,
    ministries,
    departments,
    unitTypes,
    districts,
    units,
    selectedMinistryId,
    selectedDepartmentId,
    selectedUnitTypeId,
    selectedDistrictId,
    selectedParentUnitId,
    setValue,
  ]);

  // Filter parent units based on unitType level (show units with level <= selected level)
  // Uses populated unitType from API response directly
  const parentUnitOptions = useMemo(() => {
    let filteredUnits = units.filter(u => u.id !== id && u._id !== id);

    // Scope to the selected project (+ global), mirroring the backend FK rule.
    filteredUnits = filteredUnits.filter(u => belongsToProject(u.appId));

    // If unitType is selected, filter by level using the populated unitType from API
    if (selectedUnitTypeLevel !== null) {
      filteredUnits = filteredUnits.filter(u => {
        // If unit has no unitType, exclude it (only show units with proper hierarchy)
        if (!u.unitType) return false;
        // Include units with level <= selected level (same or higher in hierarchy)
        return u.unitType.level <= selectedUnitTypeLevel;
      });
    }

    return filteredUnits.map(u => {
      // Use populated unitType from API for display
      const levelInfo = u.unitType ? ` (${u.unitType.name})` : '';
      return { value: u.id || u._id, label: `${u.name}${levelInfo}`, ...projectTag(u.appId) };
    });
  }, [units, id, selectedUnitTypeLevel, belongsToProject, projectTag]);

  // Create/Update mutation
  const mutation = useMutation({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mutationFn: (data: any) => {
      if (isEditMode && id) {
        return unitsService.update(id, data);
      }
      return unitsService.create(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['units'] });
      queryClient.invalidateQueries({ queryKey: ['assignments'] });
      localStorage.removeItem(DRAFT_KEY);
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      submittedRef.current = false;
      console.error('Error response:', error.response);

      // Use standardized error message extraction
      const errorMessage = extractErrorMessage(
        error,
        `Failed to ${isEditMode ? 'update' : 'create'} unit`
      );

      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: errorMessage,
        life: 10000,
      });
    },
  });

  // Autosave draft
  useEffect(() => {
    if (!isDirty || submittedRef.current) return;

    const timer = setTimeout(() => {
      if (submittedRef.current) return;
      localStorage.setItem(DRAFT_KEY, JSON.stringify(formValues));
    }, 1500);

    return () => clearTimeout(timer);
  }, [formValues, isDirty, DRAFT_KEY]);

  // Load draft on mount
  useEffect(() => {
    if (!isEditMode) {
      const savedDraft = localStorage.getItem(DRAFT_KEY);
      if (savedDraft) {
        try {
          const parsed = JSON.parse(savedDraft);
          setDraftData(parsed);
          setShowDraftBanner(true);
        } catch {
          localStorage.removeItem(DRAFT_KEY);
        }
      }
    }
  }, [isEditMode, DRAFT_KEY]);

  // Handle draft restore
  const handleRestoreDraft = () => {
    if (draftData) {
      reset(draftData);
      setShowDraftBanner(false);
    }
  };

  // Handle draft discard
  const handleDiscardDraft = () => {
    localStorage.removeItem(DRAFT_KEY);
    setShowDraftBanner(false);
    setDraftData(null);
  };

  // Handle form validation errors
  const onFormError = (errors: Record<string, unknown>) => {
    const errorMessages = Object.entries(errors)
      .map(([field, error]) => `${field}: ${(error as { message?: string })?.message || 'Invalid'}`)
      .join(', ');

    toast.current?.show({
      severity: 'error',
      summary: 'Validation Error',
      detail: errorMessages || 'Please fill in all required fields',
      life: 10000,
    });
  };

  // Combined loading state for button disable
  const isSaving = mutation.isPending;

  // Form submission
  const onSubmit = async (data: UnitFormData) => {
    submittedRef.current = true;
    localStorage.removeItem(DRAFT_KEY);
    // Validate user is logged in for create mode
    if (!isEditMode && !actorId) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'User not authenticated. Please log in again.',
        life: 10000,
      });
      return;
    }

    // Proceed with submission
    await submitForm(data);
  };

  /**
   * Handle File Upload
   */
  const handleFileUpload = async (file: File, districtId: string): Promise<string> => {
    try {
      setUploadProgress(0);
      const fileId = await (uploadFile as any)(
        file,
        {
          district: districtId,
          uploadedBy: actorId || 'unknown',
          module: 'CORE',
        },
        (progress: number) => {
          setUploadProgress(progress);
        }
      );
      setUploadProgress(null);
      return fileId;
    } catch (error) {
      setUploadProgress(null);
      throw error;
    }
  };

  // Submit form - build the BE-aligned payload
  const submitForm = async (data: UnitFormData) => {
    try {
      let logoId = data.logo;

      // Check if logo is a File object and needs uploading
      if (data.logo instanceof File) {
        try {
          logoId = await handleFileUpload(data.logo, data.districtId);
        } catch (error) {
          console.error('File upload failed:', error);
          toast.current?.show({
            severity: 'error',
            summary: 'Upload Error',
            detail: 'Failed to upload unit logo. Please try again.',
            life: 10000,
          });
          return; // Stop submission
        }
      }

      // Normalize phone: comma-separated input -> array of trimmed strings (blanks dropped)
      const phoneArray = data.phone
        ? data.phone.split(',').map(p => p.trim()).filter(Boolean)
        : [];

      // Build the payload with ONLY the fields the BE accepts.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const payload: any = {
        unitCode: data.unitCode,
        name: data.name,
        email: data.email,
        districtId: data.districtId,
        isVirtual: data.isVirtual ?? false,
        unitPersonnelList: (data.unitPersonnelList || []).filter(id => id && id.trim() !== ''),
      };

      // Optional fields - only include when set
      // Owning project (honoured by the BE only for callers who can access it).
      if (data.appId) payload.appId = data.appId;
      if (data.responsibleUserId) payload.responsibleUserId = data.responsibleUserId;
      if (typeof logoId === 'string' && logoId) payload.logo = logoId;
      if (data.address1) payload.address1 = data.address1;
      if (data.address2) payload.address2 = data.address2;
      if (data.city) payload.city = data.city;
      if (data.zip) payload.zip = data.zip;
      if (phoneArray.length > 0) payload.phone = phoneArray;
      if (data.responsiblePersonTitle) payload.responsiblePersonTitle = data.responsiblePersonTitle;
      if (data.unitTypeId) payload.unitTypeId = data.unitTypeId;
      if (data.departmentId) payload.departmentId = data.departmentId;
      if (data.ministryId) payload.ministryId = data.ministryId;
      if (data.proxyUserId) payload.proxyUserId = data.proxyUserId;
      if (data.parentUnitId) payload.parentUnitId = data.parentUnitId;

      // Note: createdBy/updatedBy are handled by backend from JWT token.
      // Server-computed fields (parentUnitPath, responsibleUserHistory) are NOT sent.

      await mutation.mutateAsync(payload);

      // Show success toast
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: `Unit ${isEditMode ? 'updated' : 'created'} successfully`,
        life: 3000,
      });

      // Navigate after a short delay
      setTimeout(() => {
        navigateToList();
      }, 1000);
    } catch (error) {
      // Error is already handled by mutation's onError callback
      console.error('Submission error:', error);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    handleNavigate('/units');
  };

  // Initial loading state - only for first load, not for dependent dropdown refetches
  // We check if essential data is still loading on first render
  const isInitialLoading = unitLoading ||
    (departmentsLoading && departments.length === 0) ||
    (districtsLoading && districts.length === 0) ||
    (unitsLoading && units.length === 0);

  // Show full page loading only on initial load (when no data exists yet)
  if (isInitialLoading) {
    return <PageSkeleton panels={3} />;
  }

  return (
    <PermissionGuard jobName={JOB_NAMES.UNITS}>
      <Toast ref={toast} position="top-right" />
      <div className="py-4 px-4 lg:px-6 bg-canvas dark:bg-gray-900">
        {/* Header - Compact */}
        <ScreenHeader
          icon="pi pi-building"
          title={isEditMode ? 'Edit Unit' : 'Create New Unit'}
          subtitle={isEditMode
            ? 'Update unit information'
            : preSelectedParentUnitName
              ? `Creating child unit under: ${decodeURIComponent(preSelectedParentUnitName)}`
              : 'Fill in the details below'}
          onBack={handleCancel}
        />

        {/* Upload Progress */}
        {uploadProgress !== null && (
          <div className="ds-card mb-4 p-4">
             <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
               Uploading Logo... {uploadProgress}%
             </div>
             <ProgressBar value={uploadProgress} showValue={false} style={{ height: '6px' }} />
          </div>
        )}

        {/* Existing Logo Preview */}
        {/* Image Overlay Component - Hidden by default, toggled via ref */}
        <ImagePreviewOverlay 
            ref={imageOverlayRef}
            imageUrl={previewImageUrl ?? undefined}
        />

        {/* Draft Recovery Banner */}
        {showDraftBanner && (
          <div className="mb-3 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <i className="pi pi-info-circle text-blue-600" />
              <span className="text-blue-800 dark:text-blue-200">
                Unsaved changes found. Restore them?
              </span>
            </div>
            <div className="flex gap-2">
              <Button label="Restore" size="small" onClick={handleRestoreDraft} />
              <Button label="Discard" size="small" severity="secondary" text onClick={handleDiscardDraft} />
            </div>
          </div>
        )}

        {/* Form */}
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        <form id="unit-form" onSubmit={handleSubmit(onSubmit as any, onFormError)} noValidate>
          {/* Main Form Card */}
          <div className="ds-card">

            {/* Section: Basic Information */}
            <div className="p-4 border-b border-line">
              <h2 className="text-sm font-medium text-ink-muted uppercase tracking-wide mb-3">
                Basic Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">

                {/* Project (data scope) — locked to the caller's own project unless they can access more */}
                <Controller
                  name="appId"
                  control={control}
                  render={({ field: { value, onChange, ...field } }) => (
                    <FormSelect
                      {...field}
                      value={value || ''}
                      onChange={(e) => onChange(e.target.value)}
                      label="Project"
                      options={projectOptions}
                      placeholder="Select project"
                      disabled={projectLocked || isEditMode}
                      error={!!errors.appId}
                      helperText={errors.appId?.message}
                    />
                  )}
                />

                {/* Row 1: Ministry → Department → Unit Type (cascading) */}
                <Controller
                  name="ministryId"
                  control={control}
                  render={({ field: { value, onChange, ...field } }) => (
                    <FormSelect
                      {...field}
                      value={value || ''}
                      onChange={(e) => {
                        onChange(e.target.value);
                        // Reset the downstream cascade so it can't stay inconsistent.
                        setValue('departmentId', '');
                        setValue('unitTypeId', '');
                      }}
                      label="Ministry"
                      options={ministryOptions}
                      placeholder="Select ministry"
                      filter
                      showClear
                      filterPlaceholder="Search ministry..."
                      error={!!errors.ministryId}
                      helperText={errors.ministryId?.message}
                      loading={ministriesLoading}
                    />
                  )}
                />
                 <Controller
                  name="departmentId"
                  control={control}
                  render={({ field: { value, onChange, ...field } }) => (
                    <FormSelect
                      {...field}
                      value={value || ''}
                      onChange={(e) => {
                        onChange(e.target.value);
                        // Changing department invalidates the chosen unit type.
                        setValue('unitTypeId', '');
                      }}
                      label="Department"
                      options={departmentOptions}
                      placeholder={selectedMinistryId ? 'Select department' : 'Select a ministry first'}
                      disabled={!selectedMinistryId}
                      filter
                      showClear
                      filterPlaceholder="Search department..."
                      error={!!errors.departmentId}
                      helperText={errors.departmentId?.message}
                      loading={departmentsLoading}
                    />
                  )}
                />
                <Controller
                  name="unitTypeId"
                  control={control}
                  render={({ field: { value, onChange, ...field } }) => (
                    <FormSelect
                      {...field}
                      value={value || ''}
                      onChange={(e) => onChange(e.target.value)}
                      label="Unit Type"
                      options={unitTypeOptions}
                      placeholder={selectedDepartmentId ? 'Select unit type' : 'Select a department first'}
                      disabled={!selectedDepartmentId}
                      filter
                      showClear
                      filterPlaceholder="Search unit types..."
                      error={!!errors.unitTypeId}
                      helperText={errors.unitTypeId?.message}
                      loading={unitTypesLoading}
                    />
                  )}
                />

               

                <Controller
                  name="districtId"
                  control={control}
                  render={({ field: { value, onChange, ...field } }) => (
                    <FormSelect
                      {...field}
                      value={value || ''}
                      onChange={(e) => onChange(e.target.value)}
                      label="District"
                      options={districtOptions}
                      placeholder="Select district"
                      required
                      filter
                      showClear
                      filterPlaceholder="Search districts..."
                      error={!!errors.districtId}
                      helperText={errors.districtId?.message as string}
                      loading={districtsLoading}
                    />
                  )}
                />

                {/* Row 2: Unit Name, Unit Code, Parent Unit */}
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <FormInput
                      {...field}
                      label="Unit Name"
                      placeholder="Enter unit name"
                      required
                      error={!!errors.name}
                      helperText={errors.name?.message}
                    />
                  )}
                />
                <Controller
                  name="unitCode"
                  control={control}
                  render={({ field }) => (
                    <FormInput
                      {...field}
                      value={field.value || ''}
                      label="Unit Code"
                      placeholder="e.g. HQ-001"
                      required
                      error={!!errors.unitCode}
                      helperText={errors.unitCode?.message || 'Unique code for the unit'}
                    />
                  )}
                />

                <Controller
                  name="parentUnitId"
                  control={control}
                  render={({ field: { value, onChange, ...field } }) => (
                    <FormSelect
                      {...field}
                      value={value || ''}
                      onChange={(e) => onChange(e.target.value || null)}
                      label="Parent Unit"
                      options={parentUnitOptions}
                      placeholder={selectedUnitTypeId ? 'Select parent unit' : 'Select unit type first'}
                      filter
                      showClear
                      filterPlaceholder="Search units..."
                      error={!!errors.parentUnitId}
                      helperText={
                        errors.parentUnitId?.message as string ||
                        (selectedUnitTypeLevel !== null ? `Showing units at level ${selectedUnitTypeLevel} or higher` : '')
                      }
                      loading={unitsLoading}
                    />
                  )}
                />

                {/* Row 3: Responsible User (Head), Responsible Person Title, Email */}
                <Controller
                  name="responsibleUserId"
                  control={control}
                  render={({ field: { value, onChange, ...field } }) => (
                    <FormSearchableSelect
                      {...field}
                      value={value || ''}
                      onChange={(e) => onChange(e.target.value)}
                      label="Responsible User (Head)"
                      options={responsibleUserOptions}
                      placeholder="Search and select unit head"
                      error={!!errors.responsibleUserId}
                      helperText={errors.responsibleUserId?.message}
                    />
                  )}
                />

                <Controller
                  name="responsiblePersonTitle"
                  control={control}
                  render={({ field }) => (
                    <FormInput
                      {...field}
                      value={field.value || ''}
                      label="Responsible Person Title"
                      placeholder="e.g., Superintendent of Police"
                      error={!!errors.responsiblePersonTitle}
                      helperText={errors.responsiblePersonTitle?.message}
                    />
                  )}
                />

                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <FormInput
                      {...field}
                      value={field.value || ''}
                      label="Email"
                      type="email"
                      placeholder="Enter email address"
                      required
                      error={!!errors.email}
                      helperText={errors.email?.message}
                    />
                  )}
                />

                {/* Virtual Unit toggle switch */}
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Virtual Unit
                  </label>
                  <Controller
                    name="isVirtual"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <div className="flex items-center gap-2 pt-1">
                        <span className={`text-xs ${!value ? 'font-medium text-ink' : 'text-ink-muted'}`}>No</span>
                        <InputSwitch
                          checked={value || false}
                          onChange={(e: { value: boolean }) => onChange(e.value)}
                          style={{ transform: 'scale(0.75)' }}
                        />
                        <span className={`text-xs ${value ? 'font-medium text-ink' : 'text-ink-muted'}`}>Yes</span>
                      </div>
                    )}
                  />
                </div>
              </div>
            </div>

            {/* Section: Address & Contact */}
            <div className="p-4 border-b border-line">
              <h2 className="text-sm font-medium text-ink-muted uppercase tracking-wide mb-3">
                Address & Contact
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                <Controller
                  name="address1"
                  control={control}
                  render={({ field }) => (
                    <FormInput
                      {...field}
                      value={field.value || ''}
                      label="Address Line 1"
                      error={!!errors.address1}
                      helperText={errors.address1?.message}
                    />
                  )}
                />

                <Controller
                  name="address2"
                  control={control}
                  render={({ field }) => (
                    <FormInput
                      {...field}
                      value={field.value || ''}
                      label="Address Line 2"
                      error={!!errors.address2}
                      helperText={errors.address2?.message}
                    />
                  )}
                />

                <Controller
                  name="city"
                  control={control}
                  render={({ field }) => (
                    <FormInput
                      {...field}
                      value={field.value || ''}
                      label="City"
                      error={!!errors.city}
                      helperText={errors.city?.message}
                    />
                  )}
                />

                <Controller
                  name="zip"
                  control={control}
                  render={({ field }) => (
                    <FormInput
                      {...field}
                      value={field.value || ''}
                      label="ZIP Code"
                      placeholder="123456"
                      error={!!errors.zip}
                      helperText={errors.zip?.message}
                    />
                  )}
                />

                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <FormInput
                      {...field}
                      value={field.value || ''}
                      label="Phone Numbers"
                      placeholder="Comma separated"
                      error={!!errors.phone}
                      helperText={errors.phone?.message as string}
                    />
                  )}
                />
              </div>
            </div>

            {/* Section: Additional Details */}
            <div className="p-4">
              <h2 className="text-sm font-medium text-ink-muted uppercase tracking-wide mb-3">
                Additional Details
              </h2>
              <div className="flex flex-wrap items-center justify-between gap-6">
                <Controller
                  name="logo"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <FormFileUpload
                      name="logo"
                      label="Unit Logo"
                      value={value}
                      onChange={(file) => {
                        onChange(file);
                        // Clear logo metadata when file changes
                        if (file) {
                          setLogoMeta(null);
                        }
                      }}
                      accept="image/*"
                      displayName={logoMeta?.filename}
                      onPreview={
                        !uploadProgress && typeof value === 'string' && value && logoMeta?.url
                          ? (e) => {
                              const target = e.currentTarget;
                              // Use pre-built URL directly - no API call needed
                              setPreviewImageUrl(logoMeta.url);
                              imageOverlayRef.current?.show(e, target);
                            }
                          : undefined
                      }
                    />
                  )}
                />
                {/* Action buttons */}
                <div className="flex gap-3">
                  <Button
                    type="button"
                    label="Cancel"
                    severity="secondary"
                    outlined
                    onClick={handleCancel}
                    disabled={isSaving}
                  />
                  <Button
                    type="button"
                    label={isSaving ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
                    icon={isSaving ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
                    disabled={isSaving}
                    onClick={() => {
                      handleSubmit(onSubmit as any, onFormError)();
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </form>

        {/* Leave Confirmation Dialog */}
        <DiscardChangesDialog
          visible={showLeaveDialog}
          onStay={cancelLeave}
          onLeave={confirmLeave}
          testId="UnitForm.Dialog.Leave"
        />
      </div>
    </PermissionGuard>
  );
};

export default UnitForm;
